import type { PageData } from 'types/Page';
import type { PageHealth } from 'api/new-analytics/types';
import { getAnalyticsContentHeaderShowReconnectMode } from 'app/modules/analytics/utils/analyticsContentHeaderSync';

// Stop-gap until backend exposes a stable error_code on sync failures.
// TODO: replace with ApiClientError.response / SyncBatch error_code when available.
const RECONNECT_MESSAGE_PATTERNS = [
  (message: string) => message.includes('reconnect'),
  (message: string) => message.includes('token') && message.includes('expired'),
] as const;

export function isPageHealthTokenExpired(row: PageHealth | undefined): boolean {
  return row?.health_status === 'token_expired';
}

export function isAnalyticsReconnectSyncMessage(
  message: string | null | undefined,
): boolean {
  if (message == null || message.length === 0) {
    return false;
  }
  const lower = message.toLowerCase();
  return RECONNECT_MESSAGE_PATTERNS.some((matches) => matches(lower));
}

export function doesAnalyticsSyncFailureRequireReconnect(params: {
  pageId: number;
  errorDetails: string | null | undefined;
  getPageHealth: (pageId: number) => PageHealth | undefined;
}): boolean {
  const { pageId, errorDetails, getPageHealth } = params;
  if (isPageHealthTokenExpired(getPageHealth(pageId))) {
    return true;
  }
  return isAnalyticsReconnectSyncMessage(errorDetails);
}

export function doesAnalyticsSyncSelectionRequireReconnect(params: {
  isGroupSelected: boolean;
  selectedPage: PageData | null;
  selectedPageId: number | null;
  enabledGroupPageIds: ReadonlySet<number>;
  getPageHealth: (pageId: number) => PageHealth | undefined;
}): boolean {
  const {
    isGroupSelected,
    selectedPage,
    selectedPageId,
    enabledGroupPageIds,
    getPageHealth,
  } = params;

  const selectedPageHealth =
    selectedPageId != null ? getPageHealth(selectedPageId) : undefined;

  if (
    getAnalyticsContentHeaderShowReconnectMode(
      isGroupSelected,
      selectedPage,
      selectedPageHealth,
    )
  ) {
    return true;
  }

  if (isGroupSelected) {
    return Array.from(enabledGroupPageIds).some((pageId) =>
      isPageHealthTokenExpired(getPageHealth(pageId)),
    );
  }

  return false;
}

export function doAnyPageIdsRequireAnalyticsReconnect(
  pageIds: readonly number[],
  getPageHealth: (pageId: number) => PageHealth | undefined,
): boolean {
  return pageIds.some((pageId) =>
    isPageHealthTokenExpired(getPageHealth(pageId)),
  );
}

export function doesInFlightSyncRequireReconnect(
  syncRunPageIdsNormalized: readonly number[],
  getPageHealth: (pageId: number) => PageHealth | undefined,
): boolean {
  if (syncRunPageIdsNormalized.length === 0) {
    return false;
  }
  return doAnyPageIdsRequireAnalyticsReconnect(
    syncRunPageIdsNormalized,
    getPageHealth,
  );
}

export function doesTriggerSyncErrorRequireReconnect(params: {
  pageIds: readonly number[];
  userMessage: string | undefined;
  getPageHealth: (pageId: number) => PageHealth | undefined;
}): boolean {
  const { pageIds, userMessage, getPageHealth } = params;
  if (doAnyPageIdsRequireAnalyticsReconnect(pageIds, getPageHealth)) {
    return true;
  }
  return isAnalyticsReconnectSyncMessage(userMessage);
}
